import type { MemberSearchSectionKey } from "../../Services/MemberSearch";
import { ServiceType } from "../../Enums/FirmEnum";

export type MemberSearchOption = {
  id: string;
  name: string;
  legacyId?: string | number;
  shortId?: string | number;
  /** When several offices share the same display name. */
  relatedOfficeIds?: string[];
};

export type MemberSearchWithin = "firm" | "professional";

export type MemberSearchServiceTypeFilter =
  | typeof ServiceType.Legal
  | typeof ServiceType.Accounting
  | typeof ServiceType["Investment Banking"]
  | "all";

export type MemberSearchCriteria = {
  keyword: string;
  lastName: string;
  firm?: MemberSearchOption;
  region?: MemberSearchOption;
  location?: MemberSearchOption;
  practiceIndustry?: MemberSearchOption;
  officeLocation?: MemberSearchOption;
  professionalTitle?: MemberSearchOption;
  /** Checked title boxes in Professional mode. Both, or none, mean no title filter. */
  professionalTitles?: MemberSearchOption[];
  /** Up to three regions. Within the list, values match with OR. */
  regions?: MemberSearchOption[];
  /** Up to three countries. Within the list, values match with OR. */
  locations?: MemberSearchOption[];
  searchWithin?: MemberSearchWithin;
  serviceType?: MemberSearchServiceTypeFilter;
};

export const EMPTY_MEMBER_SEARCH_CRITERIA: MemberSearchCriteria = {
  keyword: "",
  lastName: "",
};

export const EMPTY_ADVANCED_MEMBER_SEARCH_CRITERIA: MemberSearchCriteria = {
  keyword: "",
  lastName: "",
  searchWithin: "firm",
  serviceType: "all",
};

export const MEMBER_SEARCH_SERVICE_TYPE_OPTIONS: MemberSearchOption[] = [
  { id: ServiceType.Legal, name: "Legal Services" },
  { id: ServiceType["Investment Banking"], name: "Financial Services" },
  { id: ServiceType.Accounting, name: "Accounting" },
  { id: "all", name: "All" },
];

export const LEGACY_MEMBER_SEARCH_REGIONS: MemberSearchOption[] = [
  { id: "1", name: "Africa" },
  { id: "2", name: "Asia Pacific" },
  { id: "3", name: "Caribbean" },
  { id: "4", name: "Central America" },
  { id: "5", name: "Europe" },
  { id: "6", name: "Middle East" },
  { id: "7", name: "North America" },
  { id: "8", name: "South America" },
  { id: "9", name: "Latin America" },
];

export const MEMBER_SEARCH_PROFESSIONAL_TITLE_OPTIONS: MemberSearchOption[] = [
  { id: "partners-directors", name: "Partners/Directors" },
  { id: "associates-professionals", name: "Associates/Professionals" },
  { id: "all", name: "All" },
];

export const MEMBER_SEARCH_VALIDATION_FIELDS = [
  "Keyword",
  "Last Name",
  "Firm",
  "Region",
  "Location",
  "Professional Title",
  "Practice Industry",
  "Office Location",
] as const;

export const MEMBER_SEARCH_VALIDATION_FIELDS_FIRM = [
  "Firm",
  "Keyword",
  "Practice Industry",
  "Region",
  "Location",
  "Office Location",
] as const;

export const MEMBER_SEARCH_VALIDATION_FIELDS_PROFESSIONAL = [
  "Last Name",
  "Keyword",
  "Firm",
  "Office Location",
  "Practice Industry",
] as const;

export const MEMBER_SEARCH_MAX_GEO_SELECTIONS = 3;

export const capMemberSearchOptions = (
  options?: MemberSearchOption[]
): MemberSearchOption[] =>
  (options ?? [])
    .filter((option) => Boolean(option?.id?.trim()))
    .slice(0, MEMBER_SEARCH_MAX_GEO_SELECTIONS);

export const applyMemberSearchCriteriaPatch = (
  previous: MemberSearchCriteria,
  patch: Partial<MemberSearchCriteria>
): MemberSearchCriteria => {
  const next: MemberSearchCriteria = { ...previous, ...patch };

  if (patch.regions !== undefined) {
    next.regions = capMemberSearchOptions(patch.regions);
    next.region = next.regions[0];
  } else if (patch.region !== undefined) {
    next.region = patch.region?.id?.trim() ? patch.region : undefined;
    next.regions = next.region ? [next.region] : [];
  }

  if (patch.locations !== undefined) {
    next.locations = capMemberSearchOptions(patch.locations);
    next.location = next.locations[0];
  } else if (patch.location !== undefined) {
    next.location = patch.location?.id?.trim() ? patch.location : undefined;
    next.locations = next.location ? [next.location] : [];
  }

  if (patch.professionalTitles === undefined && patch.professionalTitle !== undefined) {
    const titleId = patch.professionalTitle?.id?.trim();
    if (!titleId || titleId === "all") {
      next.professionalTitle = undefined;
      next.professionalTitles = [];
    } else {
      next.professionalTitle = patch.professionalTitle;
      next.professionalTitles = [patch.professionalTitle];
    }
  }

  return next;
};

export const listMemberSearchRegions = (
  criteria: MemberSearchCriteria
): MemberSearchOption[] => {
  const listed = capMemberSearchOptions(criteria.regions);
  if (listed.length) {
    return listed;
  }
  if (criteria.region?.id?.trim()) {
    return [criteria.region];
  }
  return [];
};

export const listMemberSearchLocations = (
  criteria: MemberSearchCriteria
): MemberSearchOption[] => {
  const listed = capMemberSearchOptions(criteria.locations);
  if (listed.length) {
    return listed;
  }
  if (criteria.location?.id?.trim()) {
    return [criteria.location];
  }
  return [];
};

const optionIds = (options: MemberSearchOption[]): string[] | undefined => {
  const ids = options
    .map((option) => option.id.trim())
    .filter(Boolean);
  return ids.length ? ids : undefined;
};

const hasText = (value?: string) => Boolean(value?.trim());

const ALLOWED_SERVICE_TYPE_FILTERS: Array<
  Exclude<MemberSearchServiceTypeFilter, "all">
> = [ServiceType.Legal, ServiceType.Accounting, ServiceType["Investment Banking"]];

export const resolveMemberSearchServiceType = (
  criteria: MemberSearchCriteria
): string | undefined => {
  if (criteria.searchWithin === "professional") {
    return undefined;
  }
  const value = String(criteria.serviceType ?? "").trim();
  if (!value || value === "all") {
    return undefined;
  }
  return ALLOWED_SERVICE_TYPE_FILTERS.includes(
    value as Exclude<MemberSearchServiceTypeFilter, "all">
  )
    ? value
    : undefined;
};

export const hasAdvancedMemberSearchCriteria = (
  criteria: MemberSearchCriteria
): boolean => {
  const hasShared =
    hasText(criteria.keyword) ||
    Boolean(criteria.firm?.id) ||
    Boolean(criteria.practiceIndustry?.id) ||
    Boolean(criteria.officeLocation?.id) ||
    listMemberSearchRegions(criteria).length > 0 ||
    listMemberSearchLocations(criteria).length > 0;

  if (criteria.searchWithin === "professional") {
    return hasShared || hasText(criteria.lastName);
  }

  return hasShared;
};

export const hasMemberSearchCriteria = (criteria: MemberSearchCriteria): boolean =>
  hasText(criteria.keyword) ||
  hasText(criteria.lastName) ||
  Boolean(criteria.firm?.id) ||
  listMemberSearchRegions(criteria).length > 0 ||
  listMemberSearchLocations(criteria).length > 0 ||
  Boolean(criteria.practiceIndustry?.id) ||
  Boolean(criteria.officeLocation?.id) ||
  Boolean(
    criteria.professionalTitle?.id &&
      criteria.professionalTitle.id !== "all"
  );

export const hasMemberSearchRequestParams = (request: MemberSearchRequest): boolean =>
  Boolean(request.query) ||
  Boolean(request.firmId) ||
  Boolean(request.regionIds?.length) ||
  Boolean(request.countryIds?.length) ||
  Boolean(request.practiceIndustryId) ||
  Boolean(request.officeId) ||
  Boolean(request.officeIds?.length) ||
  Boolean(request.professionalTitleFilter) ||
  Boolean(request.serviceType);

export const resolveMemberSearchSectionPhases = (
  searchWithin?: MemberSearchWithin
): MemberSearchSectionKey[][] => {
  if (searchWithin === "professional") {
    return [["professionals"]];
  }
  if (searchWithin === "firm") {
    return [["firms"]];
  }
  return [["professionals"], ["firms", "jurisdictions"]];
};

export const flattenMemberSearchSections = (
  searchWithin?: MemberSearchWithin
): MemberSearchSectionKey[] => resolveMemberSearchSectionPhases(searchWithin).flat();

/** Keyword used by the Cosmos quick-search API (interim until full filter API exists). */
export const resolveMemberSearchQuery = (criteria: MemberSearchCriteria): string => {
  if (hasText(criteria.keyword)) {
    return criteria.keyword.trim();
  }
  if (hasText(criteria.lastName)) {
    return criteria.lastName.trim();
  }
  if (hasText(criteria.firm?.name)) {
    return criteria.firm!.name.trim();
  }
  return "";
};

/** True when the API query is driven by the Last Name field (not keyword). */
export const isLastNameOnlySearch = (criteria: MemberSearchCriteria): boolean => {
  if (!hasText(criteria.lastName) || hasText(criteria.keyword)) {
    return false;
  }
  return true;
};

export type MemberSearchRequest = {
  query: string;
  firmId?: string;
  regionIds?: string[];
  countryIds?: string[];
  practiceIndustryId?: string;
  officeId?: string;
  officeIds?: string[];
  professionalTitleFilter?: string;
  serviceType?: string;
  /** Set together with a keyword. Matches the last name as a prefix. */
  lastName?: string;
  lastNameOnly: boolean;
};

const specificProfessionalTitles = (
  criteria: MemberSearchCriteria
): MemberSearchOption[] =>
  (criteria.professionalTitles ?? []).filter(
    (option) => option.id?.trim() && option.id !== "all"
  );

export const resolveMemberSearchProfessionalTitle = (
  criteria: MemberSearchCriteria
): MemberSearchOption | undefined => {
  if (criteria.searchWithin === "firm") {
    return undefined;
  }
  if (criteria.searchWithin === "professional") {
    const selected = specificProfessionalTitles(criteria);
    if (selected.length === 1) {
      return selected[0];
    }
    if (criteria.professionalTitles) {
      return undefined;
    }
  }
  const id = criteria.professionalTitle?.id?.trim();
  if (!id || id === "all") {
    return undefined;
  }
  return criteria.professionalTitle;
};

const resolveProfessionalTitleFilter = (
  criteria: MemberSearchCriteria
): string | undefined => resolveMemberSearchProfessionalTitle(criteria)?.id?.trim();

const resolveGeographicIds = (
  criteria: MemberSearchCriteria,
  firmId?: string
): { regionIds?: string[]; countryIds?: string[] } => {
  if (criteria.searchWithin === "professional") {
    return {};
  }

  const regionIds = optionIds(listMemberSearchRegions(criteria));
  const countryIds = optionIds(listMemberSearchLocations(criteria));
  if (criteria.searchWithin !== "firm") {
    return { regionIds, countryIds };
  }
  if (firmId) {
    return {};
  }
  if (regionIds?.length) {
    return { regionIds };
  }
  return { countryIds };
};

/** Builds API params; scoped filters narrow all three result sections. */
export const buildMemberSearchRequest = (
  criteria: MemberSearchCriteria
): MemberSearchRequest => {
  const isFirmMode = criteria.searchWithin === "firm";
  const firmId = criteria.firm?.id?.trim() || undefined;
  const { regionIds, countryIds } = resolveGeographicIds(criteria, firmId);
  const practiceIndustryId = criteria.practiceIndustry?.id?.trim() || undefined;
  const officeLocation = criteria.officeLocation;
  const officeIds = officeLocation?.id?.trim()
    ? [
        ...new Set([
          officeLocation.id.trim(),
          ...(officeLocation.relatedOfficeIds ?? [])
            .map((id) => String(id ?? "").trim())
            .filter(Boolean),
        ]),
      ]
    : undefined;
  const officeId = officeIds?.[0];
  const professionalTitleFilter = isFirmMode
    ? undefined
    : resolveProfessionalTitleFilter(criteria);
  const serviceType = resolveMemberSearchServiceType(criteria);
  const lastName = isFirmMode ? "" : criteria.lastName;
  const scopedParams = {
    firmId,
    regionIds,
    countryIds,
    practiceIndustryId,
    officeId,
    officeIds,
    professionalTitleFilter,
    serviceType,
  };

  if (hasText(criteria.keyword)) {
    return {
      query: criteria.keyword.trim(),
      ...scopedParams,
      lastName: hasText(lastName) ? lastName.trim() : undefined,
      lastNameOnly: false,
    };
  }

  if (hasText(lastName)) {
    return {
      query: lastName.trim(),
      ...scopedParams,
      lastNameOnly: true,
    };
  }

  if (firmId) {
    return {
      query: "",
      ...scopedParams,
      lastNameOnly: false,
    };
  }

  if (regionIds?.length) {
    return {
      query: "",
      regionIds,
      countryIds,
      practiceIndustryId,
      officeId,
      officeIds,
      professionalTitleFilter,
      serviceType,
      lastNameOnly: false,
    };
  }

  if (countryIds?.length) {
    return {
      query: "",
      countryIds,
      practiceIndustryId,
      officeId,
      officeIds,
      professionalTitleFilter,
      serviceType,
      lastNameOnly: false,
    };
  }

  if (practiceIndustryId) {
    return {
      query: "",
      practiceIndustryId,
      officeId,
      officeIds,
      professionalTitleFilter,
      serviceType,
      lastNameOnly: false,
    };
  }

  if (officeId) {
    return {
      query: "",
      officeId,
      officeIds,
      professionalTitleFilter,
      serviceType,
      lastNameOnly: false,
    };
  }

  if (professionalTitleFilter) {
    return {
      query: "",
      professionalTitleFilter,
      serviceType,
      lastNameOnly: false,
    };
  }

  if (serviceType) {
    return {
      query: "",
      serviceType,
      lastNameOnly: false,
    };
  }

  return {
    query: resolveMemberSearchQuery(criteria),
    lastNameOnly: false,
  };
};

export type MemberSearchFilterDisplay = {
  label: string;
  value: string;
};

const formatSelectedServiceType = (
  criteria: MemberSearchCriteria
): string | undefined => {
  const serviceType = resolveMemberSearchServiceType(criteria);
  if (!serviceType) {
    return undefined;
  }
  return (
    MEMBER_SEARCH_SERVICE_TYPE_OPTIONS.find((option) => option.id === serviceType)
      ?.name ?? serviceType
  );
};

export const getMemberSearchFilterDisplays = (
  criteria: MemberSearchCriteria
): MemberSearchFilterDisplay[] => {
  const serviceType = formatSelectedServiceType(criteria);
  const regionNames = listMemberSearchRegions(criteria)
    .map((option) => option.name)
    .filter(Boolean);
  const locationNames = listMemberSearchLocations(criteria)
    .map((option) => option.name)
    .filter(Boolean);
  const professionalTitle = resolveMemberSearchProfessionalTitle(criteria);
  return [
    { label: "Keyword", value: criteria.keyword.trim() || "Keyword Search" },
    { label: "Last Name", value: criteria.lastName.trim() || "All" },
    { label: "Firm", value: criteria.firm?.name || "All Firms" },
    ...(serviceType ? [{ label: "Service Type", value: serviceType }] : []),
    { label: "Region", value: regionNames.join(", ") || "All Regions" },
    { label: "Country", value: locationNames.join(", ") || "All Countries" },
    {
      label: "Professional Title",
      value: professionalTitle?.name || "All Professional Titles",
    },
    {
      label: "Practice Industry",
      value: criteria.practiceIndustry?.name || "All Practice Industries",
    },
    {
      label: "Office Location",
      value: criteria.officeLocation?.name || "All Office Locations",
    },
  ];
};

export type MemberSearchCatalogOptions = {
  firms: MemberSearchOption[];
  regions: MemberSearchOption[];
  locations: MemberSearchOption[];
  practiceIndustries: MemberSearchOption[];
  officeLocations: MemberSearchOption[];
};

const hydrateOption = (
  option: MemberSearchOption | undefined,
  catalog: MemberSearchOption[]
): MemberSearchOption | undefined => {
  const id = option?.id?.trim();
  if (!id) {
    return undefined;
  }
  const match = catalog.find((item) => item.id === id);
  return match ? { ...option, ...match, id } : option;
};

const hydrateOptions = (
  options: MemberSearchOption[] | undefined,
  catalog: MemberSearchOption[]
): MemberSearchOption[] | undefined => {
  if (!options?.length) {
    return options;
  }
  return capMemberSearchOptions(
    options
      .map((option) => hydrateOption(option, catalog))
      .filter((option): option is MemberSearchOption => Boolean(option))
  );
};

export const hydrateMemberSearchCriteriaFromCatalogs = (
  criteria: MemberSearchCriteria,
  catalogs: MemberSearchCatalogOptions
): MemberSearchCriteria => {
  const regions = hydrateOptions(listMemberSearchRegions(criteria), catalogs.regions);
  const locations = hydrateOptions(
    listMemberSearchLocations(criteria),
    catalogs.locations
  );
  return {
    ...criteria,
    firm: hydrateOption(criteria.firm, catalogs.firms),
    regions,
    region: regions?.[0],
    locations,
    location: locations?.[0],
    practiceIndustry: hydrateOption(
      criteria.practiceIndustry,
      catalogs.practiceIndustries
    ),
    officeLocation: hydrateOption(criteria.officeLocation, catalogs.officeLocations),
  };
};

export const matchesProfessionalTitleFilter = (
  title: string | undefined,
  listTitle: string | undefined,
  unlistedTitle: string | undefined,
  listingCategory: string | undefined,
  filterId: string | undefined
): boolean => {
  if (!filterId || filterId === "all") {
    return true;
  }

  const rawTitle = [listTitle, title, unlistedTitle]
    .map((value) => String(value ?? "").trim())
    .find(Boolean)
    ?.toLowerCase() ?? "";

  const normalizedCategory = (listingCategory ?? "").toLowerCase();

  if (filterId === "partners-directors") {
    return (
      rawTitle.includes("partner") ||
      rawTitle.includes("director") ||
      normalizedCategory === "executive management"
    );
  }

  if (filterId === "associates-professionals") {
    return (
      rawTitle.includes("associate") ||
      rawTitle.includes("professional") ||
      normalizedCategory === "professionals"
    );
  }

  return true;
};
